import { z } from 'zod';
import { ModelSlug, TableColumn, TopicPath, WebUrl } from './fields.js';
import { Uuid } from './primitives.js';
/**
 * Knowledge base contracts (M2). Dates travel as ISO strings; money in paise
 * (integer) unless the field says otherwise.
 */
// ---- knowledge base: requests ----------------------------------------------------------------
export const SourceKindName = z.enum([
    'syllabus',
    'ncert',
    'notes',
    'pyq_reference',
    'news',
    'own',
    'coaching',
]);
export const SourceLicenceName = z.enum(['own', 'open', 'reference_only', 'news']);
export const SourceTypeName = z.enum([
    'pdf',
    'url',
    'feed',
    'csv',
    'xlsx',
    'text',
    'image',
    'docx',
]);
export const SourceStatusName = z.enum([
    'uploaded',
    'extracting',
    'extracted',
    'chunking',
    'chunked',
    'embedding',
    'ready',
    'failed',
    'needs_attention',
]);
/** A tag such as an exam name, subject or topic label. */
export const KbLabel = z.string().trim().min(1).max(80);
/** A page number in a source file (from 1). */
export const KbPage = z.number().int().min(1);
export const SourceTagsSchema = z
    .object({
    exams: z.array(KbLabel).max(10).default([]),
    subject: KbLabel.optional(),
    topics: z.array(KbLabel).max(20).default([]),
    year: z.number().int().min(1990).max(2100).optional(),
    shift: z.string().trim().max(40).optional(),
    /**
     * "Only for": narrows material to some exams (by pack key), optionally one level, branch or
     * variant. Empty = every exam that tests the subject (the default).
     */
    onlyFor: z
        .array(z
        .object({
        exam: KbLabel,
        level: KbLabel.optional(),
        branch: KbLabel.optional(),
        variant: KbLabel.optional(),
    })
        .strict())
        .max(10)
        .optional(),
    /**
     * Page-range topics: pages `from`–`to` cover `topic` (e.g. a book's chapters). Chunks in a
     * range get that topic; other pages keep the source's first topic.
     */
    topicRanges: z
        .array(z
        .object({
        from: KbPage,
        to: KbPage,
        topic: KbLabel,
    })
        .strict()
        .refine((r) => r.from <= r.to, { message: 'from must not be after to' }))
        .max(100)
        .optional(),
})
    .strict();
export const TableMappingSchema = z
    .object({
    question: TableColumn.optional(),
    options: z.array(TableColumn).max(5).optional(),
    answer: TableColumn.optional(),
    explanation: TableColumn.optional(),
    topic: TableColumn.optional(),
    year: TableColumn.optional(),
    shift: TableColumn.optional(),
})
    .strict();
/** Multipart upload: the `meta` field carries this JSON next to the `file` part. */
export const SourceUploadMeta = z
    .object({
    title: z.string().trim().min(1).max(300).optional(),
    kind: SourceKindName,
    licence: SourceLicenceName,
    tags: SourceTagsSchema.default({ exams: [], topics: [] }),
    mapping: TableMappingSchema.optional(),
    /** The file has Hindi text: OCR also reads Hindi (slower). */
    ocrHindi: z.boolean().optional(),
    /** Current affairs: the month the news is about, "2026-10". */
    contentMonth: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .optional(),
})
    .strict();
export const AddUrlSourceRequest = SourceUploadMeta.extend({
    url: z.string().trim().max(2000).pipe(WebUrl),
    type: z.enum(['url', 'feed']),
}).strict();
export const UpdateSourceRequest = z
    .object({
    title: z.string().trim().min(1).max(300).optional(),
    kind: SourceKindName.optional(),
    licence: SourceLicenceName.optional(),
    tags: SourceTagsSchema.optional(),
    contentMonth: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .nullable()
        .optional(),
})
    .strict();
export const SourceListQuery = z
    .object({
    status: SourceStatusName.optional(),
    type: SourceTypeName.optional(),
    q: z.string().trim().max(200).optional(),
    exam: z.string().trim().max(80).optional(),
    subject: z.string().trim().max(80).optional(),
    parentId: Uuid.optional(),
    topLevel: z.enum(['true', 'false']).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
})
    .strict();
export const PageQuery = z
    .object({
    from: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
})
    .strict();
export const EditChunkRequest = z
    .object({
    text: z.string().trim().min(20).max(12_000).optional(),
    excluded: z.boolean().optional(),
    topicPath: TopicPath.nullable().optional(),
})
    .strict();
export const SearchFiltersSchema = z
    .object({
    exam: KbLabel.optional(),
    subject: KbLabel.optional(),
    topicPath: TopicPath.optional(),
    year: z.number().int().optional(),
    kinds: z.array(SourceKindName).optional(),
    licences: z.array(SourceLicenceName).optional(),
    sourceIds: z.array(Uuid).max(50).optional(),
})
    .strict();
export const SearchRequest = z
    .object({
    query: z.string().trim().min(2).max(500),
    limit: z.number().int().min(1).max(30).optional(),
    mode: z.enum(['hybrid', 'keyword', 'vector']).optional(),
    filters: SearchFiltersSchema.optional(),
})
    .strict();
export const AskRequest = z
    .object({
    question: z.string().trim().min(3).max(1000),
    k: z.number().int().min(1).max(10).optional(),
    filters: SearchFiltersSchema.optional(),
})
    .strict();
export const StartReembedRequest = z.object({ toModel: ModelSlug }).strict();
// ---- knowledge base: responses ---------------------------------------------------------------
export const SourceDto = z.object({
    id: Uuid,
    parentId: Uuid.nullable(),
    productId: Uuid.nullable(),
    type: SourceTypeName,
    kind: SourceKindName,
    licence: SourceLicenceName,
    title: z.string(),
    uri: z.string().nullable(),
    mimeType: z.string().nullable(),
    sizeBytes: z.number().nullable(),
    hasFile: z.boolean(),
    tags: SourceTagsSchema,
    status: SourceStatusName,
    failedStep: SourceStatusName.nullable(),
    error: z.string().nullable(),
    retryCount: z.number(),
    stats: z.record(z.string(), z.number()),
    createdBy: Uuid.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    readyAt: z.string().nullable(),
    /** Current affairs month, "2026-10". */
    contentMonth: z.string().nullable(),
    keepCurrent: z.boolean(),
});
export const SourceListDto = z.object({
    items: z.array(SourceDto),
    total: z.number(),
    summary: z.record(SourceStatusName, z.number()),
});
export const SourcePageDto = z.object({
    pageNo: z.number(),
    text: z.string(),
    charCount: z.number(),
    needsOcr: z.boolean(),
    ocr: z.boolean(),
    ocrConfidence: z.number().nullable(),
});
export const TablePreviewDto = z.object({
    kind: z.enum(['csv', 'xlsx']),
    headers: z.array(z.string()),
    rows: z.array(z.array(z.string())),
    total: z.number(),
});
export const ChunkDto = z.object({
    id: Uuid,
    sourceId: Uuid,
    ord: z.number(),
    text: z.string(),
    headingPath: z.array(z.string()),
    pageFrom: z.number().nullable(),
    pageTo: z.number().nullable(),
    topicPath: z.string().nullable(),
    tokenCount: z.number(),
    excluded: z.boolean(),
    embedded: z.boolean(),
    embeddingModel: z.string().nullable(),
    embeddingDims: z.number().nullable(),
    embeddedAt: z.string().nullable(),
    edited: z.boolean(),
    updatedAt: z.string(),
});
export const ChunkDetailDto = ChunkDto.extend({
    source: z.object({
        id: Uuid,
        title: z.string(),
        type: SourceTypeName,
        uri: z.string().nullable(),
        licence: SourceLicenceName,
        hasFile: z.boolean(),
    }),
});
export const ChunkListDto = z.object({ items: z.array(ChunkDto), total: z.number() });
export const EditChunkResponse = z.object({
    chunk: ChunkDetailDto,
    reembedError: z.string().nullable(),
});
export const SearchHitDto = z.object({
    chunkId: Uuid,
    sourceId: Uuid,
    sourceTitle: z.string(),
    sourceType: z.string(),
    sourceUri: z.string().nullable(),
    hasFile: z.boolean(),
    licence: z.string(),
    text: z.string(),
    headingPath: z.array(z.string()),
    pageFrom: z.number().nullable(),
    pageTo: z.number().nullable(),
    topicPath: z.string().nullable(),
    score: z.number(),
    vectorRank: z.number().nullable(),
    keywordRank: z.number().nullable(),
    /** The source's exams tag. */
    sourceExams: z.array(z.string()).optional(),
    /** Preview as learner only: whether the hit fits the learner, and its score before the boost. */
    fits: z.boolean().optional(),
    baseScore: z.number().optional(),
});
export const SearchResponse = z.object({
    hits: z.array(SearchHitDto),
    mode: z.enum(['hybrid', 'keyword', 'vector']),
    degraded: z.string().nullable(),
    embeddingModel: z.string().nullable(),
    costPaise: z.number(),
});
/** Admin "Preview as learner": the bot's doubt ranking for an exam (and JE branch), no doubt spent. */
export const PreviewSearchRequest = z
    .object({
    query: z.string().trim().min(2).max(500),
    limit: z.number().int().min(1).max(30).optional(),
    mode: z.enum(['hybrid', 'keyword', 'vector']).optional(),
    filters: SearchFiltersSchema.optional(),
    learner: z
        .object({
        exam: z.string().trim().min(1).max(40),
        branch: z.string().trim().min(1).max(40).optional(),
    })
        .strict(),
})
    .strict();
export const PreviewSearchResponse = SearchResponse.extend({
    audience: z.object({
        key: z.string(),
        exams: z.array(z.string()),
        avoidTopics: z.array(z.string()),
    }),
    /** The multiplier applied to fitting hits. */
    boost: z.number(),
});
export const CitationDto = z.object({
    marker: z.number(),
    chunkId: Uuid,
    sourceId: Uuid,
    sourceTitle: z.string(),
    pageFrom: z.number().nullable(),
    pageTo: z.number().nullable(),
    sourceUri: z.string().nullable(),
    hasFile: z.boolean(),
    licence: z.string(),
});
export const AskResponse = z.object({
    answer: z.string(),
    citations: z.array(CitationDto),
    excerpts: z.array(SearchHitDto),
    warnings: z.object({
        uncitedSentences: z.array(z.string()),
        unknownMarkers: z.array(z.number()),
        longQuotes: z.array(z.object({ chunkId: Uuid, words: z.number() })),
    }),
    model: z.string().nullable(),
    callId: Uuid.nullable(),
    costPaise: z.number(),
    cached: z.boolean(),
});
export const ReembedJobDto = z.object({
    id: Uuid,
    fromModel: z.string().nullable(),
    toModel: z.string(),
    dims: z.number(),
    status: z.enum(['running', 'done', 'failed', 'cancelled']),
    total: z.number(),
    done: z.number(),
    error: z.string().nullable(),
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
});
export const ReembedStatusDto = z.object({
    serving: z.object({ model: z.string(), dims: z.number() }),
    embeddedChunks: z.number(),
    running: ReembedJobDto.nullable(),
    recent: z.array(ReembedJobDto),
});
//# sourceMappingURL=kb.js.map